import React from "react";
import AppMetaChip from "./AppMetaChip";

function renderSidebarIcon(icon) {
  if (!icon) {
    return null;
  }

  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      className: ["ds-sidebar__nav-icon-svg", icon.props.className].filter(Boolean).join(" "),
    });
  }

  const IconComponent = icon;
  return <IconComponent className="ds-sidebar__nav-icon-svg" />;
}

function renderProfileIcon(icon) {
  if (!icon) {
    return null;
  }

  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      className: ["ds-sidebar__profile-icon-svg", icon.props.className].filter(Boolean).join(" "),
    });
  }

  const IconComponent = icon;
  return <IconComponent className="ds-sidebar__profile-icon-svg" />;
}

export default function AppSidebar({
  className = "",
  navAriaLabel = "Hauptnavigation",
  logoSrc,
  logoAlt = "Logo",
  faviconSrc,
  groups = [],
  activeItemKey,
  onItemClick,
  profile = null,
}) {
  const classes = ["ds-sidebar-slot", className].filter(Boolean).join(" ");

  return (
    <aside className={classes} aria-label={navAriaLabel}>
      <div className="ds-sidebar">
        <div className="ds-sidebar__logo">
          {faviconSrc ? (
            <img className="ds-sidebar__logo-mark" src={faviconSrc} alt="" aria-hidden="true" />
          ) : null}
          {logoSrc ? <img className="ds-sidebar__logo-wordmark" src={logoSrc} alt={logoAlt} /> : null}
        </div>

        <div className="ds-sidebar__nav">
          {groups.map((group, groupIndex) => (
            <div
              key={group.key || group.title || `group-${groupIndex}`}
              className="ds-sidebar__nav-group"
            >
              {group.title ? (
                <div className="ds-sidebar__group-title-row" aria-hidden="true">
                  <span className="ds-sidebar__group-title-placeholder" />
                  <span className="ds-sidebar__group-title">{group.title}</span>
                </div>
              ) : null}

              <div className="ds-sidebar__nav-group-items">
                {(group.items || []).map((item) => {
                  const isActive = item.active || item.key === activeItemKey;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`ds-sidebar__nav-item${isActive ? " is-active" : ""}`}
                      aria-label={item.ariaLabel || item.label}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => onItemClick?.(item)}
                    >
                      <span className="ds-sidebar__nav-icon" aria-hidden="true">
                        {renderSidebarIcon(item.icon)}
                      </span>
                      <span className="ds-sidebar__nav-label">{item.label}</span>
                      {item.badge !== undefined && item.badge !== null ? (
                        <AppMetaChip size="xs" className="ds-sidebar__nav-badge" aria-label={item.badgeLabel}>
                          {item.badge}
                        </AppMetaChip>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {profile ? (
          <div
            className={`ds-sidebar__profile${profile.onClick ? " is-interactive" : ""}`}
            role={profile.onClick ? "button" : undefined}
            tabIndex={profile.onClick ? 0 : undefined}
            onClick={profile.onClick}
            onKeyDown={profile.onClick ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                profile.onClick();
              }
            } : undefined}
          >
            <span className="ds-sidebar__profile-icon" aria-hidden="true">
              {renderProfileIcon(profile.icon)}
            </span>
            <div className="ds-sidebar__profile-copy">
              <div className="ds-sidebar__profile-name">{profile.name}</div>
              <div className="ds-sidebar__profile-role">{profile.role}</div>
            </div>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
